import { FunctionComponent, PropsWithChildren, useEffect, useRef } from 'react';

import { GameFooterConnected, useGameStateSlice, useBetSlice, useCoreSelector, useConfigSlice , useModifiers, useBonusBalanceConfig} from '@pp/core';
import { useNetPositionConnected } from '@pp/core/src/hooks/useNetPositionConnected';


import { InlineRecentResultsConnected } from '../../recentResults';
import { GameGridContentConnected } from '../content/GameGridContentConnected';
import { useGameGridContext } from '../GameGridContext';
import { MWGameGridObserver } from '../MWGameGridObserver';

import styles from './GameGridWrapper.module.css';

import { useGameDispatch } from '../../../store/GameStore';
import { mwStateActions } from '../../../store/slices/mwState/slice';
import { useIsPortraitMax } from '../../../hooks/useWindowDimension';
import { MWSpecificGameStatus } from '../../../store/slices/mwState/types';

export interface GameGridWrapperConnectedProps extends PropsWithChildren {}

export const GameGridWrapperConnected: FunctionComponent<GameGridWrapperConnectedProps> = ({ children }) => {
  const dispatch = useGameDispatch();
  const gameGridWrapperRef = useRef<HTMLDivElement>(null);
  const balance = useGameStateSlice.getDeductibleBalance();
  const isLowBalance = useBetSlice.isLowBalance(balance);
  const isBetsOpenStatus = useGameStateSlice.isBetsOpenStatus();
  const compliance = useConfigSlice.compliance();
  const displayBonusBalance = useBonusBalanceConfig();
  const net = useNetPositionConnected();
  const mods = useModifiers(styles, 'footerWithBonusRow', {
    visible: compliance?.showSessionTimer || displayBonusBalance || net !== undefined || compliance?.showSessionId,
  });
  const isPortraitMax = useIsPortraitMax();
  const gameStatus = useCoreSelector((state) => state.gameState.currentStatus);

  const {
    setters: { setGameGridWrapperRefState },
  } = useGameGridContext();

  const handleRemoveResultMessage = () => {
    if (
      gameStatus.startsWith('RESULT_') ||
      gameStatus === MWSpecificGameStatus.YOU_WIN ||
      gameStatus === MWSpecificGameStatus.MEGA_WIN
    ) {
      dispatch(mwStateActions.setHideWinMessage(true));
    }
  };
  useEffect(() => {
    if (setGameGridWrapperRefState) {
      setGameGridWrapperRefState(gameGridWrapperRef);
    }
  }, [setGameGridWrapperRefState]);

  useEffect(() => {
    MWGameGridObserver.addMeasurer('gameGridWrapper', () => {
      if (!gameGridWrapperRef.current) return { gameGridWrapperHeight: null };
      const shiftOffset = isBetsOpenStatus && !isLowBalance && isPortraitMax ? 46 : 0;
      return {
        gameGridWrapperHeight: gameGridWrapperRef.current.getBoundingClientRect().height - shiftOffset,
      };
    });
  }, [gameGridWrapperRef]);

  return (
    <div className={styles.gameGridWrapper} ref={gameGridWrapperRef}>
      <div className={styles.removeResultWrapper} onClick={handleRemoveResultMessage}></div>
      <div className={`${styles.gameGridWrapper__grid}`}>
        <GameGridContentConnected className={styles.gameGridWrapper__content}>{children}</GameGridContentConnected>
        <div className={styles.gameGridWrapper__recentResults}>
          <InlineRecentResultsConnected />
        </div>
        <div className={`${styles.gameGridWrapper__footer} ${styles.gameGridWrapper__footer_landscape}`}>
          <GameFooterConnected />
        </div>
      </div>
      <div className={`${styles.gameGridWrapper__footer} ${styles.gameGridWrapper__footer_portrait} ${mods}`}>
        <GameFooterConnected />
      </div>
    </div>
  );
};
